import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography,
  Alert
} from '@mui/material';
import { Warning, Lock, Security } from '@mui/icons-material';
import { useAuthContext } from '../../contexts/AuthContext';
import { stripeService } from '../../services/stripeService';
import { PUBLIC_PLANS, formatPrice, publicPlanForFeature } from '../../config/pricing';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import trialService from '../../services/trialService';
import { planLimitsService } from '../../services/limits/planLimitsService';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  feature?: string; // Feature específica a verificar (ex: 'pharmacy', etc)
}

/**
 * SubscriptionGuard - Componente que protege rotas verificando:
 * 1. Trial de 7 dias (para novos usuários)
 * 2. Assinatura Stripe ativa (para usuários pagantes)
 * 3. Feature do plano (quando `feature` é informado)
 */
const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ children, feature }) => {
  const { currentUser, loading: authLoading, trialData, logout } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false); // Iniciar como false para segurança
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);
  const [blockReason, setBlockReason] = useState<'subscription' | 'feature'>('subscription');
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);

  // Política de retenção de dados (LGPD)
  const RETENTION_POLICY = {
    SUSPENSION_DAYS: 90, // Período de suspensão após trial expirado
    FINAL_DELETION_DAYS: 90, // Dias após suspensão para exclusão definitiva
    TOTAL_RETENTION_DAYS: 180, // Total: 90 + 90
    MEDICAL_RECORDS_YEARS: 20, // Prazo legal CFM
    WARNING_DAYS: 30 // Aviso antes da exclusão
  };

  // Função para calcular data de exclusão
  const calculateDeletionDate = (trialExpiredDate: Date): Date => {
    const deletionDate = new Date(trialExpiredDate);
    deletionDate.setDate(deletionDate.getDate() + RETENTION_POLICY.TOTAL_RETENTION_DAYS);
    return deletionDate;
  };

  // Função para formatar data
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Função para calcular dias restantes
  const getDaysUntilDeletion = (deletionDate: Date): number => {
    const now = new Date();
    const diff = deletionDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  // Calcular data de exclusão se trial expirado
  const deletionDate = trialData?.isExpired && trialData?.expiredAt
    ? calculateDeletionDate(new Date(trialData.expiredAt))
    : trialData?.isExpired
    ? calculateDeletionDate(new Date())
    : null;
  
  const daysUntilDeletion = deletionDate ? getDaysUntilDeletion(deletionDate) : null;
  const isWarningPeriod = daysUntilDeletion !== null && daysUntilDeletion <= RETENTION_POLICY.WARNING_DAYS;

  // Memoizar valores primitivos do trialData para evitar re-renders desnecessários
  const trialIsExpired = useMemo(() => trialData?.isExpired ?? false, [trialData?.isExpired]);
  const trialTimeRemaining = useMemo(() => trialData?.timeRemaining ?? 0, [trialData?.timeRemaining]);
  const trialDaysRemaining = useMemo(() => trialData?.daysRemaining ?? 0, [trialData?.daysRemaining]);
  
  // Log inicial para debug - executar apenas em desenvolvimento e com throttling
  // Usar useMemo para evitar recálculos desnecessários
  const logData = React.useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      // Throttle logs para evitar spam no console
      const now = Date.now();
      if (!(window as any).__lastSubscriptionGuardLog || now - (window as any).__lastSubscriptionGuardLog > 2000) {
        (window as any).__lastSubscriptionGuardLog = now;
        return {
          currentUser: currentUser?.email,
          authLoading,
          trialIsExpired,
          trialTimeRemaining,
          location: location.pathname,
          loading,
          hasAccess
        };
      }
    }
    return null;
  }, [currentUser?.email, authLoading, trialIsExpired, trialTimeRemaining, location.pathname, loading, hasAccess]);

  React.useEffect(() => {
    if (logData && process.env.NODE_ENV === 'development') {
      console.log('📋 [SUBSCRIPTION-GUARD] Estado no render:', logData);
    }
  }, [logData]);

  // Função para verificar status da assinatura Stripe
  // Usar useCallback para evitar recriação desnecessária
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      console.log('🔍 [SUBSCRIPTION-GUARD] Verificando assinatura Stripe...');
      
      const subscriptionInfo = await stripeService.getSubscriptionInfo();
      setSubscriptionStatus(subscriptionInfo);
      
      console.log('📊 [SUBSCRIPTION-GUARD] Status da assinatura:', {
        hasActiveSubscription: subscriptionInfo.hasActiveSubscription,
        isTrial: subscriptionInfo.isTrial,
        subscriptions: subscriptionInfo.subscriptions
      });
      
      return subscriptionInfo.hasActiveSubscription;
    } catch (error) {
      console.error('❌ [SUBSCRIPTION-GUARD] Erro ao verificar assinatura:', error);
      return false;
    }
  }, []);

  // Função para verificar se usuário é funcionário (subordinate_user)
  const checkIfEmployee = async (userId: string): Promise<{ isEmployee: boolean; adminId?: string; clinicId?: string }> => {
    try {
      console.log('🔍 [SUBSCRIPTION-GUARD] Verificando se é funcionário subordinado:', userId);
      
      // 1. Verificar em subordinate_users PRIMEIRO (mais comum para subordinados)
      const auth = (await import('../../config/firebase')).auth;
      const currentFirebaseUser = auth.currentUser;
      
      if (currentFirebaseUser?.email) {
        try {
          const subordinateQuery = query(
            collection(db, 'subordinate_users'),
            where('email', '==', currentFirebaseUser.email)
          );
          
          const subordinateSnapshot = await getDocs(subordinateQuery);
          
          if (!subordinateSnapshot.empty) {
            const subordinateData = subordinateSnapshot.docs[0].data();
            console.log('✅ [SUBSCRIPTION-GUARD] ★ Subordinate user encontrado:', {
              email: currentFirebaseUser.email,
              matricula: subordinateData.matricula,
              adminId: subordinateData.adminId,
              clinicId: subordinateData.clinicId
            });
            
            return { 
              isEmployee: true, 
              adminId: subordinateData.adminId, 
              clinicId: subordinateData.clinicId 
            };
          }
        } catch (subordinateError: any) {
          // Silenciar - não é crítico
          if (subordinateError?.code !== 'permission-denied') {
            console.warn('⚠️ [SUBSCRIPTION-GUARD] Erro em subordinate_users:', subordinateError.code);
          }
        }
      }
      
      // 2. Verificar no documento users/{uid} (apenas se não encontrou em subordinate_users)
      try {
        console.log('🔍 [SUBSCRIPTION-GUARD] Buscando em users/ por UID:', userId);
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.matricula || userData.adminId || userData.isSubordinate) {
            console.log('✅ [SUBSCRIPTION-GUARD] Funcionário encontrado em users/');
            return { 
              isEmployee: true, 
              adminId: userData.adminId, 
              clinicId: userData.clinicId 
            };
          }
          console.log('ℹ️ [SUBSCRIPTION-GUARD] Documento users/ encontrado mas não é subordinado');
        } else {
          console.log('ℹ️ [SUBSCRIPTION-GUARD] Documento users/ não existe');
        }
      } catch (userError: any) {
        // Ignorar erro de permissão - é normal para subordinate_users
        console.warn('⚠️ [SUBSCRIPTION-GUARD] Erro ao buscar users/ (ignorando):', userError.code);
      }
      
      console.log('ℹ️ [SUBSCRIPTION-GUARD] Não é funcionário subordinado');
      return { isEmployee: false };
    } catch (error) {
      console.error('❌ [SUBSCRIPTION-GUARD] Erro fatal ao verificar funcionário:', error);
      return { isEmployee: false };
    }
  };

  // Função para encontrar o dono da clínica (usuário com mesmo clinicId mas sem adminId)
  const findClinicOwner = async (clinicId: string): Promise<string | null> => {
    try {
      console.log('🔍 [SUBSCRIPTION-GUARD] Buscando dono da clínica:', clinicId);
      
      // Buscar usuários com mesmo clinicId
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('clinicId', '==', clinicId));
      const snapshot = await getDocs(q);
      
      // Encontrar o dono (usuário sem adminId e sem isSubordinate)
      for (const docSnap of snapshot.docs) {
        const userData = docSnap.data();
        if (!userData.adminId && !userData.isSubordinate && !userData.matricula) {
          console.log('✅ [SUBSCRIPTION-GUARD] Dono da clínica encontrado:', docSnap.id);
          return docSnap.id;
        }
      }
      
      console.warn('⚠️ [SUBSCRIPTION-GUARD] Dono da clínica não encontrado');
      return null;
    } catch (error) {
      console.error('❌ [SUBSCRIPTION-GUARD] Erro ao buscar dono da clínica:', error);
      return null;
    }
  };

  // Função para verificar acesso do dono da clínica
  const checkClinicOwnerAccess = async (ownerId: string, ownerEmail: string): Promise<boolean> => {
    try {
      console.log('🔍 [SUBSCRIPTION-GUARD] Verificando acesso do dono:', ownerId);
      
      // Verificar assinatura Stripe do dono
      const subscriptionsRef = collection(db, 'customers', ownerId, 'subscriptions');
      const subscriptionsSnapshot = await getDocs(subscriptionsRef);
      
      if (!subscriptionsSnapshot.empty) {
        const subscriptions: any[] = [];
        subscriptionsSnapshot.forEach((doc) => {
          subscriptions.push({ id: doc.id, ...doc.data() });
        });
        
        const activeStatuses = ['active', 'trialing'];
        const hasActiveSubscription = subscriptions.some(sub => 
          activeStatuses.includes(sub.status)
        );
        
        if (hasActiveSubscription) {
          console.log('✅ [SUBSCRIPTION-GUARD] Dono tem assinatura ativa');
          return true;
        }
      }
      
      // Verificar trial do dono
      const trialInfo = await trialService.getTrialInfo(ownerId, ownerEmail);
      if (trialInfo && !trialInfo.isExpired && trialInfo.timeRemaining > 0) {
        console.log('✅ [SUBSCRIPTION-GUARD] Dono tem trial ativo');
        return true;
      }
      
      console.log('🚫 [SUBSCRIPTION-GUARD] Dono não tem acesso');
      return false;
    } catch (error) {
      console.error('❌ [SUBSCRIPTION-GUARD] Erro ao verificar acesso do dono:', error);
      return false;
    }
  };

  // Verificar acesso do usuário
  useEffect(() => {
    console.log('🔄 [SUBSCRIPTION-GUARD] useEffect executado');
    console.log('📊 [SUBSCRIPTION-GUARD] Dependências:', {
      currentUser: currentUser?.email,
      authLoading,
      trialData: trialData ? { isExpired: trialData.isExpired } : null,
      pathname: location.pathname
    });
    
    const verifyAccess = async () => {
      console.log('🔐 [SUBSCRIPTION-GUARD] Iniciando verificação de acesso...');
      
      // Se ainda está carregando auth, aguardar
      if (authLoading) {
        console.log('⏳ [SUBSCRIPTION-GUARD] Aguardando autenticação...');
        return;
      }

      // Se não há usuário logado, não tem acesso
      if (!currentUser) {
        console.log('👤 [SUBSCRIPTION-GUARD] Usuário não autenticado');
        setHasAccess(false);
        setLoading(false);
        return;
      }

      // Usuários admin (@upclinic.com) sempre têm acesso
      if (currentUser.email?.endsWith('@upclinic.com')) {
        console.log('👑 [SUBSCRIPTION-GUARD] Admin detectado - acesso liberado');
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // VERIFICAÇÃO CRÍTICA: Verificar status de bloqueio e isenção
      // APENAS se o documento users/ existir (não existe para subordinate_users)
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          console.log('🔍 [SUBSCRIPTION-GUARD] Status do usuário:', {
            isento: userData.isento,
            bloqueado: userData.bloqueado,
            userId: currentUser.uid
          });
          
          // Se usuário tem isenção, liberar acesso imediatamente
          if (userData.isento === true) {
            console.log('✅ [SUBSCRIPTION-GUARD] Usuário isento - acesso liberado');
            setHasAccess(true);
            setLoading(false);
            return;
          }
          
          // Se usuário está bloqueado (e não é isento), negar acesso
          if (userData.bloqueado === true) {
            console.log('🚫 [SUBSCRIPTION-GUARD] Usuário bloqueado - acesso negado');
            setHasAccess(false);
            setShowExpiredDialog(true);
            setLoading(false);
            return;
          }
        } else {
          // Documento não existe - pode ser subordinate_user, continuar verificação
          console.log('ℹ️ [SUBSCRIPTION-GUARD] Documento users/ não existe (pode ser subordinate_user)');
        }
      } catch (error: any) {
        // Silenciar erro de permissão - é normal para subordinate_users
        if (error?.code !== 'permission-denied') {
          console.error('❌ [SUBSCRIPTION-GUARD] Erro ao verificar status:', error);
        }
      }

      // NOVA LÓGICA: Verificar se é funcionário subordinado
      const employeeCheck = await checkIfEmployee(currentUser.uid);
      
      if (employeeCheck.isEmployee) {
        console.log('👤 [SUBSCRIPTION-GUARD] Funcionário subordinado detectado!');
        console.log('👤 [SUBSCRIPTION-GUARD] AdminId:', employeeCheck.adminId);
        console.log('👤 [SUBSCRIPTION-GUARD] ClinicId:', employeeCheck.clinicId);
        
        if (employeeCheck.adminId) {
          // Buscar email do admin para passar ao getTrialInfo
          let adminEmail = '';
          try {
            const adminDocRef = doc(db, 'users', employeeCheck.adminId);
            const adminDoc = await getDoc(adminDocRef);
            if (adminDoc.exists()) {
              adminEmail = adminDoc.data().email || '';
            }
          } catch (error: any) {
            // Silenciar erro de permissão - pode não ter acesso ao documento do admin
            if (error?.code !== 'permission-denied') {
              console.warn('⚠️ [SUBSCRIPTION-GUARD] Erro ao buscar email do admin:', error);
            }
            // Continuar sem email do admin - checkClinicOwnerAccess pode funcionar sem ele
          }
          
          // Verificar acesso do admin
          const adminHasAccess = await checkClinicOwnerAccess(employeeCheck.adminId, adminEmail);
          
          if (adminHasAccess) {
            console.log('✅ [SUBSCRIPTION-GUARD] Admin tem acesso - liberando para funcionário');
            setHasAccess(true);
            setLoading(false);
            return;
          } else {
            console.log('🚫 [SUBSCRIPTION-GUARD] Admin não tem acesso - bloqueando funcionário');
            setHasAccess(false);
            setShowExpiredDialog(true);
            setLoading(false);
            return;
          }
        } else {
          console.warn('⚠️ [SUBSCRIPTION-GUARD] Funcionário sem adminId - bloqueando');
          setHasAccess(false);
          setShowExpiredDialog(true);
          setLoading(false);
          return;
        }
      }

      console.log('🔐 [SUBSCRIPTION-GUARD] Verificando acesso para:', currentUser.email);
      console.log('📋 [SUBSCRIPTION-GUARD] trialData recebido:', trialData);

      // 1) Assinatura / trial — acesso básico ao app
      const hasStripeSubscription = await checkSubscriptionStatus();

      if (hasStripeSubscription) {
        console.log('✅ [SUBSCRIPTION-GUARD] Assinatura Stripe ativa');
      } else if (trialData) {
        const isExpired = trialIsExpired;
        const timeRemaining = trialTimeRemaining;
        const isActive = !isExpired && timeRemaining > 0;

        console.log('📊 [SUBSCRIPTION-GUARD] Status do trial:', {
          isExpired,
          isActive,
          timeRemaining,
          daysRemaining: trialDaysRemaining
        });

        if (!isActive) {
          console.log('⏰ [SUBSCRIPTION-GUARD] Trial expirado ou inativo - bloqueando acesso');
          setBlockReason('subscription');
          setHasAccess(false);
          setShowExpiredDialog(true);
          setLoading(false);
          return;
        }
        console.log(`✅ [SUBSCRIPTION-GUARD] Trial ativo (${trialDaysRemaining} dias restantes)`);
      } else {
        console.log('⚠️ [SUBSCRIPTION-GUARD] Sem trial no contexto - tentando obter/instanciar trial');
        try {
          await trialService.startTrial(currentUser.uid, currentUser.email || '');
          const trialInfo = await trialService.getTrialInfo(currentUser.uid, currentUser.email || '');
          if (!(trialInfo && !trialInfo.isExpired && trialInfo.timeRemaining > 0)) {
            console.log('🚫 [SUBSCRIPTION-GUARD] Sem assinatura e sem trial válido - bloqueando acesso');
            setBlockReason('subscription');
            setHasAccess(false);
            setShowExpiredDialog(true);
            setLoading(false);
            return;
          }
          console.log(`✅ [SUBSCRIPTION-GUARD] Trial ativo (${trialInfo.daysRemaining} dias)`);
        } catch (trialError) {
          console.warn('⚠️ [SUBSCRIPTION-GUARD] Erro ao obter/instanciar trial:', trialError);
          setBlockReason('subscription');
          setHasAccess(false);
          setShowExpiredDialog(true);
          setLoading(false);
          return;
        }
      }

      // 2) Feature do plano (só depois do acesso básico)
      if (feature) {
        try {
          const hasFeature = await planLimitsService.isFeatureAvailable(feature as any);

          if (!hasFeature) {
            console.log(`🚫 [SUBSCRIPTION-GUARD] Feature '${feature}' não disponível no plano atual`);
            setBlockReason('feature');
            setHasAccess(false);
            setShowExpiredDialog(true);
            setLoading(false);
            return;
          }

          console.log(`✅ [SUBSCRIPTION-GUARD] Feature '${feature}' disponível`);
        } catch (error) {
          console.error(`❌ [SUBSCRIPTION-GUARD] Erro ao verificar feature '${feature}':`, error);
        }
      }

      setBlockReason('subscription');
      setHasAccess(true);
      setLoading(false);
    };

    // ✅ Envolver verifyAccess em try-catch para capturar erros não tratados
    verifyAccess().catch((error: any) => {
      // Silenciar erros de permissão - é normal para subordinate_users
      if (error?.code === 'permission-denied') {
        console.debug('ℹ️ [SUBSCRIPTION-GUARD] Erro de permissão em verifyAccess (normal para subordinados)');
        // Se for subordinado, liberar acesso mesmo com erro de permissão
        setHasAccess(true);
        setLoading(false);
        return;
      }
      console.error('❌ [SUBSCRIPTION-GUARD] Erro não tratado em verifyAccess:', error);
      // Em caso de erro não esperado, negar acesso por segurança
      setHasAccess(false);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, authLoading, trialIsExpired, trialTimeRemaining, location.pathname, checkSubscriptionStatus]);

  // Listener em tempo real para mudanças no documento do usuário
  useEffect(() => {
    if (!currentUser?.uid || authLoading) return;

    // ✅ Verificar se é subordinado antes de configurar listener
    // Subordinados não têm documento users/ próprio, então não precisam do listener
    let unsubscribe: (() => void) | null = null;
    let isSubordinate = false;

    const setupListener = async () => {
      try {
        const employeeCheck = await checkIfEmployee(currentUser.uid);
        if (employeeCheck.isEmployee) {
          console.log('ℹ️ [SUBSCRIPTION-GUARD] Subordinado detectado - não configurando listener para users/');
          isSubordinate = true;
          return; // Subordinados não precisam do listener
        }
      } catch (error) {
        // Se der erro ao verificar, continuar e tentar configurar listener
        console.warn('⚠️ [SUBSCRIPTION-GUARD] Erro ao verificar se é subordinado:', error);
      }

      if (isSubordinate) return;

      console.log('👂 [SUBSCRIPTION-GUARD] Configurando listener para mudanças do usuário:', currentUser.uid);
      
      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        unsubscribe = onSnapshot(
          userDocRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const userData = snapshot.data();
              console.log('🔄 [SUBSCRIPTION-GUARD] Documento do usuário atualizado em tempo real:', {
                isento: userData.isento,
                bloqueado: userData.bloqueado,
                userId: currentUser.uid
              });
              
              // Se status mudou, re-verificar acesso imediatamente
              if (userData.isento === true) {
                console.log('✅ [SUBSCRIPTION-GUARD] Isenção detectada via listener - liberando acesso');
                setHasAccess(true);
                setLoading(false);
                setShowExpiredDialog(false);
              } else if (userData.bloqueado === true && userData.isento !== true) {
                console.log('🚫 [SUBSCRIPTION-GUARD] Bloqueio detectado via listener - negando acesso');
                setHasAccess(false);
                setShowExpiredDialog(true);
                setLoading(false);
              }
            }
          },
          (error: any) => {
            // ✅ Silenciar erro de permissão - é normal para subordinate_users
            if (error?.code === 'permission-denied') {
              console.debug('ℹ️ [SUBSCRIPTION-GUARD] Erro de permissão no listener (normal para subordinados)');
              return;
            }
            console.warn('⚠️ [SUBSCRIPTION-GUARD] Erro no listener do documento do usuário:', error);
          }
        );
      } catch (error: any) {
        // ✅ Capturar erro antes que seja lançado como "Uncaught"
        if (error?.code === 'permission-denied') {
          console.debug('ℹ️ [SUBSCRIPTION-GUARD] Erro de permissão ao configurar listener (normal para subordinados)');
          return;
        }
        console.error('❌ [SUBSCRIPTION-GUARD] Erro ao configurar listener:', error);
      }
    };

    setupListener();
    
    return () => {
      if (unsubscribe) {
        console.log('🔇 [SUBSCRIPTION-GUARD] Removendo listener do documento do usuário');
        unsubscribe();
      }
    };
    // Usar apenas currentUser?.uid para evitar re-renders desnecessários
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, authLoading]);

  // Função para redirecionar para checkout
  const handleGoToCheckout = () => {
    setShowExpiredDialog(false);
    navigate(`/checkout?plan=${publicPlanForFeature(feature)}`);
  };

  // Função para fazer logout
  const handleLogout = async () => {
    try {
      console.log('🚪 [SUBSCRIPTION-GUARD] Iniciando logout...');
      setShowExpiredDialog(false);
      await logout();
      console.log('✅ [SUBSCRIPTION-GUARD] Logout realizado, redirecionando para login');
      navigate('/login');
    } catch (error) {
      console.error('❌ [SUBSCRIPTION-GUARD] Erro ao fazer logout:', error);
      // Mesmo com erro, redirecionar para login
      navigate('/login');
    }
  };

  // Se está carregando, mostrar spinner
  if (loading || authLoading) {
    console.log('⏳ [SUBSCRIPTION-GUARD] Mostrando loading...');
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Verificando acesso...
        </Typography>
      </Box>
    );
  }

  // Se não tem acesso e não é página pública, mostrar diálogo
  if (!hasAccess) {
    console.log('🚫 [SUBSCRIPTION-GUARD] SEM ACESSO - Mostrando modal de bloqueio');
    
    return (
      <>
        <Dialog 
          open={showExpiredDialog}
          onClose={() => {}} // Impedir fechar clicando fora
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            backgroundColor: blockReason === 'feature' ? 'info.main' : 'error.light',
            color: blockReason === 'feature' ? 'info.contrastText' : 'error.contrastText'
          }}>
            <Lock />
            {blockReason === 'feature' ? 'Upgrade necessário' : 'Acesso Bloqueado'}
          </DialogTitle>
          
          <DialogContent sx={{ mt: 2 }}>
            {blockReason === 'feature' ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom fontWeight="bold">
                  Recurso disponível em um plano superior
                </Typography>
                <Typography variant="body2">
                  Este módulo não está incluído no seu plano atual. Faça upgrade para liberar o acesso.
                </Typography>
              </Alert>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body1" gutterBottom fontWeight="bold">
                    Seu período de teste expirou
                  </Typography>
                  <Typography variant="body2">
                    {trialData?.isExpired 
                      ? 'Seu teste gratuito de 7 dias expirou. Para continuar usando o sistema, assine um de nossos planos.'
                      : 'Você não possui uma assinatura ativa. Assine um plano para acessar o sistema.'
                    }
                  </Typography>
                </Alert>

                <Alert 
                  severity="error" 
                  icon={<Security />}
                  sx={{ 
                    mb: 2,
                    backgroundColor: 'error.dark',
                    color: 'error.contrastText',
                    '& .MuiAlert-icon': {
                      color: 'error.contrastText'
                    }
                  }}
                >
                  <Typography variant="body2" fontWeight="bold" gutterBottom sx={{ fontSize: '1rem' }}>
                    ⚠️ AVISO IMPORTANTE - POLÍTICA DE RETENÇÃO E EXCLUSÃO DE DADOS (LGPD)
                  </Typography>
                  
                  <Typography variant="body2" paragraph sx={{ mt: 1 }}>
                    <strong>Seu acesso está SUSPENSO.</strong> Caso você não reative sua conta ou não assine um plano, 
                    suas informações serão <strong>excluídas automaticamente</strong> após o prazo de retenção.
                  </Typography>
                  
                  {deletionDate && (
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      <strong>Data Limite para Reativação:</strong> {formatDate(deletionDate)}
                      {isWarningPeriod && daysUntilDeletion !== null && (
                        <Box component="span" sx={{ ml: 1, fontSize: '0.9rem' }}>
                          (⚠️ {daysUntilDeletion} {daysUntilDeletion === 1 ? 'dia restante' : 'dias restantes'}!)
                        </Box>
                      )}
                    </Typography>
                  )}
                  
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      navigate('/data-retention-policy');
                    }}
                    sx={{ 
                      mt: 1,
                      color: 'error.contrastText',
                      borderColor: 'error.contrastText',
                      '&:hover': {
                        borderColor: 'error.contrastText',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    Ver Política Completa de Retenção de Dados
                  </Button>
                </Alert>
              </>
            )}

            <Box sx={{ p: 2, backgroundColor: 'grey.100', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Planos disponíveis:
              </Typography>
              {PUBLIC_PLANS.map((plan) => (
                <Typography key={plan.id} variant="body2" sx={{ mb: 1 }}>
                  • <strong>{plan.name}:</strong> {formatPrice(plan.monthly)}/mês - {plan.tagline}
                </Typography>
              ))}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              {blockReason === 'feature'
                ? 'Após o upgrade, o recurso será liberado imediatamente'
                : 'Após assinar, você terá acesso imediato ao sistema'}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ p: 2, gap: 1 }}>
            {blockReason === 'feature' ? (
              <Button
                onClick={() => {
                  setShowExpiredDialog(false);
                  navigate(-1);
                }}
                variant="outlined"
                color="inherit"
              >
                Voltar
              </Button>
            ) : (
              <Button 
                onClick={handleLogout} 
                variant="outlined"
                color="inherit"
              >
                Sair
              </Button>
            )}
            <Button 
              onClick={handleGoToCheckout}
              variant="contained"
              color="primary"
              size="large"
              sx={{ flexGrow: 1 }}
            >
              {blockReason === 'feature' ? 'Fazer Upgrade' : 'Assinar Agora'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Conteúdo bloqueado com overlay */}
        <Box sx={{ 
          filter: 'blur(8px)', 
          pointerEvents: 'none',
          opacity: 0.3,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          {children}
        </Box>
      </>
    );
  }

  // Se tem acesso, renderizar conteúdo normalmente
  console.log('✅ [SUBSCRIPTION-GUARD] ACESSO LIBERADO - Renderizando children');
  return <>{children}</>;
};

export default SubscriptionGuard;

